import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { User } from "lucide-react";

import {
  buyerSignOut,
  fetchBuyerMe,
  fetchBuyerReservations,
  type BuyerProfile,
  type BuyerReservationSummary,
} from "@/features/buyerAuth";

// TNK-167 phase C — buyer account dashboard: profile header + the buyer's
// reservations as floating-shadow cards (the [[TNK-165]] recipe). The list
// comes from GET /api/buyer/reservations (phase B); each card links to the
// account-scoped detail route /shop/account/reservations/:id.
export const Route = createFileRoute("/shop/account/")({
  component: ShopAccountIndex,
});

// Mirrors STATUS_STYLE in shop.r.$token.tsx — same lifecycle states (base +
// TNK-127 phase 2). Kept local to avoid coupling the two route files.
const STATUS_STYLE: Record<string, string> = {
  Pending: "bg-warning text-warning-foreground",
  Accepted: "bg-success text-success-foreground",
  Declined: "bg-destructive/15 text-destructive",
  Expired: "bg-muted text-muted-foreground",
  Completed: "bg-success text-success-foreground",
  UnderReview: "bg-secondary text-secondary-foreground",
  OfferSent: "bg-warning text-warning-foreground",
  AwaitingPayment: "bg-primary text-primary-foreground",
  Paid: "bg-success text-success-foreground",
  Cancelled: "bg-destructive/15 text-destructive",
};

// TNK-165 — soft drop-shadow so each reservation reads as a floating card.
const FLOAT_SHADOW = "0 6px 12px -2px rgba(0,0,0,0.28)";

const PICKUP_LABEL: Record<
  BuyerReservationSummary["pickupPreference"],
  string
> = {
  pickup: "Pickup",
  ship: "Ship",
  either: "Pickup or ship",
};

function ShopAccountIndex() {
  const navigate = useNavigate();
  const [buyer, setBuyer] = useState<BuyerProfile | null | undefined>(
    undefined,
  );
  // null = not loaded yet; [] = loaded, none. Distinct from the load error.
  const [reservations, setReservations] = useState<
    BuyerReservationSummary[] | null
  >(null);
  const [resError, setResError] = useState<string | null>(null);

  useEffect(() => {
    void fetchBuyerMe().then(setBuyer);
  }, []);

  // Load reservations once we know there's a signed-in buyer. Guarding on
  // `buyer` keeps us from firing the requireBuyer-gated endpoint while auth
  // is still resolving (undefined) or when signed out (null).
  useEffect(() => {
    if (!buyer) return;
    let cancelled = false;
    void fetchBuyerReservations()
      .then((rs) => {
        if (!cancelled) setReservations(rs);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setResError(
            e instanceof Error ? e.message : "Couldn't load your reservations.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [buyer]);

  if (buyer === undefined) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (buyer === null) {
    return (
      <div className="mx-auto max-w-md py-12">
        <div
          className="space-y-5 rounded-lg border border-border bg-card p-6 text-center"
          style={{ boxShadow: FLOAT_SHADOW }}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <User className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h1 className="shop-display text-2xl font-bold text-foreground">
              Your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in or create a free account to place reservations, track
              their status, and get the seller’s offers. It’s passwordless — we
              email you a one-tap link.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Link
              to="/shop/account/login"
              search={{ return: undefined, mode: undefined }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Sign in
            </Link>
            <Link
              to="/shop/account/login"
              search={{ return: undefined, mode: "register" }}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Create an account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-6 py-12">
      <div className="space-y-1 text-center">
        <h1 className="shop-display text-2xl font-bold text-foreground">
          Welcome{buyer.name ? `, ${buyer.name}` : ""}
        </h1>
        <p className="text-sm text-muted-foreground">{buyer.email}</p>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          Your reservations
        </h2>

        {reservations === null && !resError && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {resError && <p className="text-sm text-destructive">{resError}</p>}
        {reservations !== null && reservations.length === 0 && (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            You haven't made any reservations yet.
          </p>
        )}
        {reservations?.map((r) => (
          <Link
            key={r.id}
            to="/shop/account/reservations/$id"
            params={{ id: r.id }}
            className="block rounded-lg border border-border bg-card p-4 transition-transform hover:-translate-y-0.5"
            style={{ boxShadow: FLOAT_SHADOW }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"
                    }`}
                  >
                    {r.status}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.itemCount} card{r.itemCount !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Requested {new Date(r.requestedAt).toLocaleDateString()} ·{" "}
                  {PICKUP_LABEL[r.pickupPreference]}
                </div>
                {r.status === "Pending" && (
                  <div className="text-[11px] text-muted-foreground">
                    Held until {new Date(r.expiresAt).toLocaleDateString()}
                  </div>
                )}
              </div>
              {r.currentOffer && (
                <div className="shrink-0 text-right">
                  <div
                    className="shop-mono text-base font-bold"
                    style={{ color: "var(--color-primary)" }}
                  >
                    ${r.currentOffer.total.toFixed(2)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    offer {r.currentOffer.status}
                  </div>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <Link
          to="/shop/inventory"
          className="rounded-md border border-border bg-background px-4 py-2 text-center text-sm font-medium text-foreground hover:bg-muted"
        >
          Continue browsing
        </Link>
        <button
          onClick={async () => {
            await buyerSignOut();
            void navigate({
              to: "/shop/account/login",
              search: { return: undefined },
            });
          }}
          className="rounded-md border border-border bg-background px-4 py-2 text-center text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
